import {
	Alert,
	Badge,
	Button,
	Group,
	Loader,
	Paper,
	Select,
	Stack,
	Table,
	Tabs,
	Text,
	Title,
	UnstyledButton,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { parseApplicationsRefreshMs } from "@/features/applications/lib/applications-refresh";
import {
	type QueueSort,
	toggleQueueSort,
} from "@/features/applications/lib/queue-controls";
import {
	useApplicationsCountsQuery,
	useApplicationsQuery,
	useApplicationsSearchParams,
} from "@/features/applications/lib/use-applications-query";
import { useApplicationsRefresh } from "@/features/applications/lib/use-applications-refresh";
import { ApplicationsFilters } from "@/features/applications/ui/applications-filters";
import { describeApiErrorSync } from "@/shared/api/error-message";
import { StatusBadge } from "@/shared/ui/status-badge";

function fullName(
	name: string | null,
	surname: string | null,
	empty: string,
): string {
	return [name, surname].filter(Boolean).join(" ") || empty;
}

function pendingTotal(counts?: { DISCOUNT: number; CHILD: number }) {
	if (!counts) {
		return 0;
	}
	return counts.DISCOUNT + counts.CHILD;
}

function SortHeader({
	label,
	order,
	active,
	onSort,
}: {
	label: string;
	order?: "ASC" | "DESC";
	active: boolean;
	onSort: () => void;
}) {
	const mark = active ? (order === "ASC" ? " ↑" : " ↓") : "";

	return (
		<Table.Th>
			<UnstyledButton onClick={onSort}>
				{label}
				{mark}
			</UnstyledButton>
		</Table.Th>
	);
}

export function ApplicationsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { params, updateParams } = useApplicationsSearchParams();
	const { refreshMs, setRefreshMs } = useApplicationsRefresh();
	const listQuery = useApplicationsQuery(params, refreshMs);
	const countsQuery = useApplicationsCountsQuery(refreshMs);
	const lastPending = useRef<number | null>(null);

	useEffect(() => {
		const next = pendingTotal(countsQuery.data);
		const prev = lastPending.current;
		if (
			refreshMs > 0 &&
			prev !== null &&
			next > prev &&
			!countsQuery.isLoading
		) {
			notifications.show({
				color: "blue",
				title: t("applications.newRequestsTitle"),
				message: t("applications.newRequestsBody"),
			});
		}
		if (countsQuery.data) {
			lastPending.current = next;
		}
	}, [countsQuery.data, countsQuery.isLoading, refreshMs, t]);

	const onTabChange = (value: string | null) => {
		updateParams({ type: value === "CHILD" ? "CHILD" : "DISCOUNT" });
	};

	const onSort = (column: QueueSort) => {
		updateParams(toggleQueueSort(params, column));
	};

	const page = params.page ?? 1;
	const errorMessage = listQuery.error
		? describeApiErrorSync(listQuery.error)
		: null;
	const updatedAt = listQuery.dataUpdatedAt
		? new Date(listQuery.dataUpdatedAt).toLocaleTimeString("uk-UA", {
				hour: "2-digit",
				minute: "2-digit",
				second: "2-digit",
			})
		: null;

	return (
		<Stack gap="md">
			<Group justify="space-between" align="flex-end" wrap="wrap" gap="md">
				<div>
					<Title order={2}>{t("applications.title")}</Title>
					<Text c="dimmed" size="sm">
						{t("applications.subtitle")}
					</Text>
				</div>
				<Group gap="sm" align="flex-end">
					<Select
						label={t("applications.refreshLabel")}
						w={{ base: "100%", sm: 180 }}
						data={[
							{ value: "0", label: t("applications.refreshOff") },
							{ value: "15000", label: t("applications.refresh15s") },
							{ value: "30000", label: t("applications.refresh30s") },
							{ value: "60000", label: t("applications.refresh1m") },
							{ value: "120000", label: t("applications.refresh2m") },
						]}
						value={String(refreshMs)}
						onChange={(value) => {
							if (value != null) {
								setRefreshMs(parseApplicationsRefreshMs(value));
							}
						}}
						allowDeselect={false}
					/>
				</Group>
			</Group>

			<Tabs
				value={params.type}
				onChange={onTabChange}
				styles={{
					tab: {
						flexWrap: "nowrap",
						flexDirection: "row",
						alignItems: "center",
					},
					tabLabel: { whiteSpace: "nowrap" },
				}}
			>
				<Tabs.List>
					<Tabs.Tab
						value="DISCOUNT"
						rightSection={
							countsQuery.data ? (
								<Badge size="sm" variant="filled" circle>
									{countsQuery.data.DISCOUNT}
								</Badge>
							) : undefined
						}
					>
						{t("applications.nationalTab")}
					</Tabs.Tab>
					<Tabs.Tab
						value="CHILD"
						rightSection={
							countsQuery.data ? (
								<Badge size="sm" variant="filled" circle>
									{countsQuery.data.CHILD}
								</Badge>
							) : undefined
						}
					>
						{t("applications.childrenTab")}
					</Tabs.Tab>
				</Tabs.List>
			</Tabs>

			<Paper p="md" radius="lg" withBorder>
				<Stack gap="md">
					<ApplicationsFilters params={params} onChange={updateParams} />

					{listQuery.isLoading ? (
						<Group justify="center" py="xl">
							<Loader />
						</Group>
					) : null}

					{errorMessage ? (
						<Alert color="red" title={t("applications.loadError")}>
							{errorMessage}
						</Alert>
					) : null}

					{listQuery.data && listQuery.data.data.length === 0 ? (
						<Text c="dimmed" ta="center" py="xl">
							{t("applications.empty")}
						</Text>
					) : null}

					{listQuery.data && listQuery.data.data.length > 0 ? (
						<Table.ScrollContainer type="native" minWidth={720}>
							<Table highlightOnHover verticalSpacing="sm">
								<Table.Thead>
									<Table.Tr>
										<SortHeader
											label={t("applications.applicant")}
											order={params.order}
											active={params.sort === "name"}
											onSort={() => onSort("name")}
										/>
										<Table.Th>{t("common.phone")}</Table.Th>
										<Table.Th>{t("common.type")}</Table.Th>
										<SortHeader
											label={t("common.status")}
											order={params.order}
											active={params.sort === "status"}
											onSort={() => onSort("status")}
										/>
										<SortHeader
											label={t("applications.submitted")}
											order={params.order}
											active={params.sort === "createdAt"}
											onSort={() => onSort("createdAt")}
										/>
										<SortHeader
											label={t("applications.statusUpdated")}
											order={params.order}
											active={params.sort === "updatedAt"}
											onSort={() => onSort("updatedAt")}
										/>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>
									{listQuery.data.data.map((row) => (
										<Table.Tr
											key={row.id}
											style={{ cursor: "pointer" }}
											onClick={() => navigate(`/applications/${row.id}`)}
										>
											<Table.Td>
												{fullName(
													row.submitter.name,
													row.submitter.surname,
													t("common.dash"),
												)}
											</Table.Td>
											<Table.Td>{row.submitter.phone}</Table.Td>
											<Table.Td>
												{row.type === "CHILD"
													? t("applications.children")
													: (row.category?.title ??
														t("applications.nationalDiscount"))}
											</Table.Td>
											<Table.Td>
												<StatusBadge status={row.status} />
											</Table.Td>
											<Table.Td>
												{new Date(row.createdAt).toLocaleString("uk-UA")}
											</Table.Td>
											<Table.Td>
												{new Date(row.updatedAt).toLocaleString("uk-UA")}
											</Table.Td>
										</Table.Tr>
									))}
								</Table.Tbody>
							</Table>
						</Table.ScrollContainer>
					) : null}

					<Group justify="space-between">
						<Group gap="xs">
							<Text size="sm" c="dimmed">
								{t("common.page")} {page}
							</Text>
							{updatedAt ? (
								<Text size="sm" c="dimmed">
									· {t("applications.updatedAt", { time: updatedAt })}
								</Text>
							) : null}
						</Group>
						<Group>
							<Button
								variant="default"
								disabled={page <= 1 || listQuery.isLoading}
								onClick={() => updateParams({ page: Math.max(1, page - 1) })}
							>
								{t("common.previous")}
							</Button>
							<Button
								variant="default"
								disabled={!listQuery.data?.hasNextPage || listQuery.isLoading}
								onClick={() => updateParams({ page: page + 1 })}
							>
								{t("common.next")}
							</Button>
						</Group>
					</Group>
				</Stack>
			</Paper>
		</Stack>
	);
}
